import styled from '@emotion/styled'
import { ApiLandingPageVideo, ApiVideo } from '@storefronts/common-types'
import React, {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react'
import { AnalyticsContext } from '../../../../context/analyticsContext'
import {
    CustomizationContext,
    MEDIA_BORDER_RADIUS_VAR,
} from '../../../../context/CustomizationContextProvider'
import {
    FullScreenVideoContext,
    StorefrontPlaylist,
} from '../../../../context/fullScreenVideoContext'
import { VideoContext } from '../../../../context/viddyVideoContext'
import {
    inScrollingLayout,
    pageBleed,
    sectionMedia,
} from '../../../../style/scrollingLayout'
import { Constants } from '../../../../style/styleConstants'
import { SlideContent } from './CarouselSlide'
import { SingleVideoPlayer } from './SingleVideoPlayer'
import { DesktopVideoRow } from './DesktopVideoRow'
import { usePrefersReducedMotion } from '../../../../hooks/usePrefersReducedMotion'
import { useElementWidth, useInView } from '../../../../hooks/useInView'
import { useMinWidth } from '../../../../hooks/useMinWidth'

const AUTO_ROTATE_INTERVAL_MS = 5000
const TRANSITION_DURATION_MS = 500
const SLIDE_WIDTH_RATIO = 0.68

// 9:16 at page width would exceed the viewport.
const MAX_SLIDE_WIDTH = Math.round(
    Constants.CONTENT_BLOCK_DESKTOP_WIDTH * SLIDE_WIDTH_RATIO,
)
const SLIDE_GAP = 10

const CarouselWrapper = styled.div({
    position: 'relative',
    width: '100%',
    overflow: 'hidden',
    ...inScrollingLayout(pageBleed),
})

const CarouselTrack = styled.div({
    display: 'flex',
    alignItems: 'center',
})

const SlideWrapper = styled.div({
    flex: '0 0 auto',
    marginRight: `${SLIDE_GAP}px`,
    borderRadius: `var(${MEDIA_BORDER_RADIUS_VAR})`,
    overflow: 'hidden',
})

type StorefrontVideo = ApiLandingPageVideo | ApiVideo

function buildInfiniteSlides(videos: StorefrontVideo[]): StorefrontVideo[] {
    const last = videos[videos.length - 1]
    const first = videos[0]
    return [last, ...videos, first]
}

const MultiVideoCarousel: React.FC<{
    playlist: StorefrontPlaylist
}> = ({ playlist }) => {
    const videoCount = playlist.videos.length
    const reducedMotion = usePrefersReducedMotion()
    const firstIndex = 1
    /* eslint-disable react-you-might-not-need-an-effect/no-event-handler */
    const slides = useMemo(
        () => buildInfiniteSlides([...playlist.videos]),
        [playlist.videos],
    )
    /* eslint-enable react-you-might-not-need-an-effect/no-event-handler */
    const [activeIndex, setActiveIndex] = useState(firstIndex)
    const [animate, setAnimate] = useState(true)
    const autoRotateRef = useRef<ReturnType<typeof setInterval> | null>(null)
    const containerRef = useRef<HTMLDivElement>(null)
    const isTouchingRef = useRef(false)
    const isInView = useInView(containerRef, { threshold: 0.1 })
    const isInViewRef = useRef(isInView)
    isInViewRef.current = isInView
    const containerWidth = useElementWidth(containerRef)
    const slideWidth = Math.round(
        Math.min(containerWidth * SLIDE_WIDTH_RATIO, MAX_SLIDE_WIDTH),
    )

    const { setCurrentIdx, setCurrentPlaylist } = useContext(
        FullScreenVideoContext,
    )
    const { setIsMuted, setVideoTapped, videoTapped } = useContext(VideoContext)
    const { Analytics } = useContext(AnalyticsContext)
    const activeIndexRef = useRef(firstIndex)
    const videoTappedRef = useRef(videoTapped)
    videoTappedRef.current = videoTapped

    const totalSlideStep = slideWidth + SLIDE_GAP

    const computeOffset = useCallback(
        (idx: number) => {
            const centerOffset = (containerWidth - slideWidth) / 2
            return centerOffset - idx * totalSlideStep
        },
        [containerWidth, slideWidth, totalSlideStep],
    )

    const toRealVideoIndex = useCallback(
        (idx: number) =>
            (((idx - firstIndex) % videoCount) + videoCount) % videoCount,
        [videoCount, firstIndex],
    )

    const goToSlide = useCallback((idx: number) => {
        setAnimate(true)
        setActiveIndex(idx)
    }, [])

    const goToNext = useCallback(() => {
        if (!isInViewRef.current || isTouchingRef.current) return
        const current = activeIndexRef.current
        if (current >= slides.length - 1 || current <= 0) return
        setAnimate(true)
        setActiveIndex(current + 1)
    }, [slides.length])

    /* eslint-disable react-you-might-not-need-an-effect/no-event-handler */
    useEffect(() => {
        const lastSlideIndex = slides.length - 1
        const delay = reducedMotion ? 0 : TRANSITION_DURATION_MS
        if (activeIndex >= lastSlideIndex) {
            const timer = setTimeout(() => {
                setAnimate(false)
                setActiveIndex(1)
            }, delay)
            return () => clearTimeout(timer)
        }
        if (activeIndex <= 0) {
            const timer = setTimeout(() => {
                setAnimate(false)
                setActiveIndex(videoCount)
            }, delay)
            return () => clearTimeout(timer)
        }
    }, [activeIndex, videoCount, slides.length, reducedMotion])
    /* eslint-enable react-you-might-not-need-an-effect/no-event-handler */

    useEffect(() => {
        if (reducedMotion || !slideWidth) return
        autoRotateRef.current = setInterval(goToNext, AUTO_ROTATE_INTERVAL_MS)
        return () => {
            if (autoRotateRef.current) clearInterval(autoRotateRef.current)
        }
    }, [goToNext, reducedMotion, slideWidth])

    const resetAutoRotate = useCallback(() => {
        if (autoRotateRef.current) clearInterval(autoRotateRef.current)
        if (reducedMotion) return
        autoRotateRef.current = setInterval(goToNext, AUTO_ROTATE_INTERVAL_MS)
    }, [goToNext, reducedMotion])

    useEffect(() => {
        activeIndexRef.current = activeIndex
    })

    const onSlideClick = useCallback(
        (idx: number) => {
            if (idx !== activeIndexRef.current) {
                goToSlide(idx)
                resetAutoRotate()
                return
            }
            const realIdx = toRealVideoIndex(idx)
            setCurrentPlaylist(playlist)
            setCurrentIdx(realIdx)
            if (!videoTappedRef.current) {
                setVideoTapped(true)
                setIsMuted(false)
            }
            const v = playlist.videos[realIdx]
            Analytics.TRACK_CAROUSEL_EXPAND(playlist, v.name, v.videoHash)
        },
        [
            playlist,
            goToSlide,
            resetAutoRotate,
            toRealVideoIndex,
            setCurrentPlaylist,
            setCurrentIdx,
            setIsMuted,
            setVideoTapped,
            Analytics,
        ],
    )

    const touchStartX = useRef(0)
    const touchStartY = useRef(0)
    const didSwipeRef = useRef(false)

    const onTouchStart = (e: React.TouchEvent) => {
        isTouchingRef.current = true
        didSwipeRef.current = false
        touchStartX.current = e.touches[0].clientX
        touchStartY.current = e.touches[0].clientY
    }

    const onTouchEnd = (e: React.TouchEvent) => {
        isTouchingRef.current = false
        const deltaX = e.changedTouches[0].clientX - touchStartX.current
        const deltaY = e.changedTouches[0].clientY - touchStartY.current
        const isTooShort = Math.abs(deltaX) < 30
        const isVertical = Math.abs(deltaY) > Math.abs(deltaX)
        if (isTooShort || isVertical) return
        didSwipeRef.current = true
        const current = activeIndexRef.current
        if (deltaX < 0) {
            goToSlide(current + 1)
        } else {
            goToSlide(current - 1)
        }
        resetAutoRotate()
    }

    const onTouchCancel = () => {
        isTouchingRef.current = false
    }

    const onCarouselClick = (e: React.MouseEvent) => {
        if (didSwipeRef.current) {
            e.stopPropagation()
            didSwipeRef.current = false
        }
    }

    if (!slideWidth || !containerWidth) {
        return <CarouselWrapper ref={containerRef} style={{ minHeight: 300 }} />
    }

    const trackStyle = {
        transform: `translateX(${computeOffset(activeIndex)}px)`,
        transition:
            animate && !reducedMotion
                ? `transform ${TRANSITION_DURATION_MS}ms ease-in-out`
                : 'none',
    }

    return (
        <CarouselWrapper
            ref={containerRef}
            onTouchStart={onTouchStart}
            onTouchEnd={onTouchEnd}
            onTouchCancel={onTouchCancel}
            onClickCapture={onCarouselClick}
        >
            <CarouselTrack style={trackStyle}>
                {slides.map((v, i) => {
                    const isCentered = i === activeIndex
                    const shouldAnimate =
                        isCentered && isInView && !reducedMotion
                    const preload = i === activeIndex || i === activeIndex + 1
                    return (
                        <SlideWrapper
                            key={`${v.videoHash}-${i}`}
                            style={{
                                width: slideWidth,
                                transform: isCentered
                                    ? 'scale(1)'
                                    : 'scale(0.88)',
                                transition: reducedMotion
                                    ? 'none'
                                    : `transform ${TRANSITION_DURATION_MS}ms ease-in-out`,
                            }}
                        >
                            <SlideContent
                                onSlideClick={onSlideClick}
                                slideWidth={slideWidth}
                                v={v}
                                idx={i}
                                isActive={shouldAnimate}
                                shouldPreload={preload}
                                priority={i === firstIndex}
                            />
                        </SlideWrapper>
                    )
                })}
            </CarouselTrack>
        </CarouselWrapper>
    )
}

const SingleVideoWrapper = styled.div<{ fullBleed: boolean }>(
    ({ fullBleed }) => ({
        // The section itself is inline, so a padded single video re-applies the
        // content margin the host skipped.
        ...(fullBleed
            ? inScrollingLayout(pageBleed)
            : {
                  paddingLeft: Constants.CONTENT_BLOCK_MOBILE_MARGIN,
                  paddingRight: Constants.CONTENT_BLOCK_MOBILE_MARGIN,
              }),
        // Hold column width at 9:16. Declared last so the cap outranks the
        // bleed once the section is wider than a single portrait video.
        [sectionMedia(Constants.CONTENT_BLOCK_DESKTOP_WIDTH)]: {
            maxWidth: Constants.CONTENT_BLOCK_DESKTOP_WIDTH,
            marginInline: 'auto',
        },
    }),
)

/** Routes to multi-video carousel or single inline player based on playlist size */
const Carousel: React.FC<{ playlist: StorefrontPlaylist }> = ({ playlist }) => {
    const { fullBleedMedia = true } = useContext(CustomizationContext)
    const wide = useMinWidth(Constants.EXPANDED_BREAKPOINT)

    if (playlist.videos.length > 1) {
        // A wide track shows several videos at once, where a moving carousel
        // with cloned end slides reads as the same video twice; the narrow
        // one shows one and keeps wrapping.
        return wide ? (
            <DesktopVideoRow playlist={playlist} />
        ) : (
            <MultiVideoCarousel playlist={playlist} />
        )
    }
    if (playlist.videos.length === 1) {
        return (
            <SingleVideoWrapper fullBleed={fullBleedMedia}>
                <SingleVideoPlayer playlist={playlist} />
            </SingleVideoWrapper>
        )
    }

    return null
}

export default Carousel
